import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHART_FRAMEWORK_LABELS,
  CHART_KIND_LABELS,
  CHART_KINDS,
  TAX_CODES,
  type ChartAccount,
  type ChartCategory,
  type ChartImportResult,
  type ChartKind,
  type ChartTotals,
  type ChartTree,
  formatCents,
} from '@kontor/shared';
import { api, ApiError } from '../../api';
import { InfoHint } from '../../components/InfoHint';
import { useSession } from '../../session';
import './chart.css';

type Selection = { type: 'category' | 'account'; id: string } | null;
type Editor =
  | { type: 'category'; mode: 'create'; parentId: string | null }
  | { type: 'category'; mode: 'edit'; id: string }
  | { type: 'account'; mode: 'create'; categoryId: string | null }
  | { type: 'account'; mode: 'edit'; id: string }
  | null;

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/** Ein Baum aus Kategorien mit ihren Unterkategorien und Konten */
interface Node {
  category: ChartCategory;
  children: Node[];
  accounts: ChartAccount[];
}

function buildTree(tree: ChartTree, visible: (a: ChartAccount) => boolean, query: string): Node[] {
  const byParent = new Map<string | null, ChartCategory[]>();
  for (const c of tree.categories) byParent.set(c.parentId, [...(byParent.get(c.parentId) ?? []), c]);
  const accounts = new Map<string, ChartAccount[]>();
  for (const a of tree.accounts) if (visible(a)) accounts.set(a.categoryId, [...(accounts.get(a.categoryId) ?? []), a]);
  const make = (c: ChartCategory): Node => ({ category: c, children: (byParent.get(c.id) ?? []).map(make), accounts: accounts.get(c.id) ?? [] });
  const nodes = (byParent.get(null) ?? []).map(make);
  if (!query) return nodes;
  // Bei einer Suche bleiben nur Kategorien mit Treffern, im Namen oder in einer Unterkategorie
  const q = query.toLocaleLowerCase('de-DE');
  const keep = (n: Node): Node | null => {
    const children = n.children.map(keep).filter((x): x is Node => !!x);
    const own = n.category.name.toLocaleLowerCase('de-DE').includes(q);
    return children.length > 0 || n.accounts.length > 0 || own ? { ...n, children } : null;
  };
  return nodes.map(keep).filter((x): x is Node => !!x);
}

/**
 * Kontenplan (KON-162): Kategorien, Unterkategorien und Konten in einem Baum, mit einer Aktionsleiste für alles, was man zur Pflege braucht: neu anlegen, bearbeiten,
 * verschieben, ordnen, aktivieren oder deaktivieren, löschen, suchen und filtern, auf- und zuklappen, CSV exportieren und importieren und den Kontenrahmen wechseln.
 * Ein Tipp auf eine Zeile wählt sie aus, die Leiste zeigt dann die Aktionen dazu. Verwendete Konten lassen sich nicht löschen und nicht umnummerieren, nur deaktivieren.
 */
type Totals = ChartTotals['accounts'] | null;

/** Netto-Summe einer Kategorie samt Unterkategorien, null ohne Belege */
function sumOf(node: Node, totals: Totals): { cents: number; count: number } | null {
  if (!totals) return null;
  let cents = 0;
  let count = 0;
  for (const a of node.accounts) {
    const t = totals[a.number];
    if (t) {
      cents += t.netCents;
      count += t.count;
    }
  }
  for (const child of node.children) {
    const t = sumOf(child, totals);
    if (t) {
      cents += t.cents;
      count += t.count;
    }
  }
  return count > 0 ? { cents, count } : null;
}

export function ChartPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('chart.write');
  const canDelete = can('chart.delete');
  const [selection, setSelection] = useState<Selection>(null);
  const [editor, setEditor] = useState<Editor>(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'' | ChartKind>('');
  const [showInactive, setShowInactive] = useState(false);
  // Jahr der Summen, leer = alle Jahre seit Beginn
  const [year, setYear] = useState('');
  const showTotals = can('chart.report');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [imported, setImported] = useState<ChartImportResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const chart = useQuery({ queryKey: ['chart'], queryFn: async () => (await api<{ data: ChartTree }>('GET', '/chart')).data });
  const tree = chart.data;
  // Konten je Kategorie samt allen Unterkategorien (die Zahl am Kategoriekopf zählt nicht nur die direkten Konten)
  const deepCounts = useMemo(() => {
    const direct = new Map<string, number>();
    for (const a of tree?.accounts ?? []) direct.set(a.categoryId, (direct.get(a.categoryId) ?? 0) + 1);
    const children = new Map<string, string[]>();
    for (const c of tree?.categories ?? []) if (c.parentId) children.set(c.parentId, [...(children.get(c.parentId) ?? []), c.id]);
    const total = (id: string): number => (direct.get(id) ?? 0) + (children.get(id) ?? []).reduce((n, child) => n + total(child), 0);
    return new Map((tree?.categories ?? []).map((c) => [c.id, total(c.id)]));
  }, [tree]);
  const totals = useQuery({
    queryKey: ['chart-totals', year],
    queryFn: async () => (await api<{ data: ChartTotals }>('GET', `/chart/totals${year ? `?year=${year}` : ''}`)).data,
    enabled: showTotals,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
  const store = (data: ChartTree) => {
    client.setQueryData(['chart'], data);
    client.invalidateQueries({ queryKey: ['chart-options'] });
    setNotice(null);
  };
  const fail = (e: unknown, fallback: string) => setNotice({ ok: false, text: errorText(e, fallback) });

  const action = useMutation({
    mutationFn: async (run: () => Promise<{ data: ChartTree }>) => (await run()).data,
    onSuccess: store,
    onError: (e) => fail(e, 'Das hat nicht geklappt.'),
  });
  const run = (call: () => Promise<{ data: ChartTree }>) => action.mutate(call);

  const importCsv = useMutation({
    mutationFn: async (csv: string) => (await api<{ data: ChartImportResult & { tree: ChartTree } }>('POST', '/chart/import', { csv })).data,
    onSuccess: (data) => {
      store(data.tree);
      setImported(data);
    },
    onError: (e) => fail(e, 'Die Datei konnte nicht gelesen werden.'),
  });
  async function pickFile(file: File | undefined) {
    if (!file) return;
    setImported(null);
    importCsv.mutate(await file.text());
  }

  const nodes = useMemo(
    () =>
      tree
        ? buildTree(
            tree,
            (a) => (showInactive || a.active) && (!kind || a.kind === kind) && (!query || `${a.number} ${a.name} ${a.description ?? ''}`.toLocaleLowerCase('de-DE').includes(query.toLocaleLowerCase('de-DE'))),
            query,
          ).filter((n) => !kind || n.category.kind === kind)
        : [],
    [tree, showInactive, kind, query],
  );

  if (chart.isLoading) return <p className="muted">Wird geladen …</p>;
  if (chart.isError || !tree) return <div className="alert alert--error">{(chart.error as Error)?.message ?? 'Der Kontenplan konnte nicht geladen werden.'}</div>;

  const categories = new Map(tree.categories.map((c) => [c.id, c]));
  const accountById = new Map(tree.accounts.map((a) => [a.id, a]));
  const selectedCategory = selection?.type === 'category' ? categories.get(selection.id) : undefined;
  const selectedAccount = selection?.type === 'account' ? accountById.get(selection.id) : undefined;
  const siblings = (c: ChartCategory) => tree.categories.filter((x) => x.parentId === c.parentId);
  const pathOf = (id: string): string => {
    const c = categories.get(id)!;
    return c.parentId ? `${pathOf(c.parentId)} › ${c.name}` : c.name;
  };
  const allIds = tree.categories.map((c) => c.id);
  const mismatch = tree.framework !== tree.settingsFramework;

  const toggle = (id: string) => setCollapsed((s) => (s.has(id) ? new Set([...s].filter((x) => x !== id)) : new Set([...s, id])));

  return (
    <>
      <div className="page-head">
        <h1>Kontenplan</h1>
        <span className="muted">{tree.accounts.length} Konten · {CHART_FRAMEWORK_LABELS[tree.framework]}</span>
      </div>
      {mismatch && (
        <div className="alert" role="status">
          In Einrichtung → Finanzen steht {CHART_FRAMEWORK_LABELS[tree.settingsFramework]}, der Plan beruht aber auf {CHART_FRAMEWORK_LABELS[tree.framework]}.{' '}
          {can('setup.write') ? <Link to="/einrichtung/kontenplan">Kontenrahmen wechseln</Link> : 'Bitten Sie jemanden mit dem Recht für die Einrichtung, den Kontenrahmen zu wechseln.'}
        </div>
      )}
      {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--error'}`} role={notice.ok ? 'status' : 'alert'}>{notice.text}</div>}
      {imported && (
        <div className={`alert ${imported.errors.length ? '' : 'alert--ok'}`} role="status">
          <strong>Import:</strong> {imported.created} neu, {imported.updated} geändert, {imported.unchanged} unverändert{imported.skipped ? `, ${imported.skipped} nicht übernommen` : ''}.
          {imported.errors.length > 0 && (
            <ul className="chart-errors">
              {imported.errors.slice(0, 10).map((e) => <li key={e.line}>Zeile {e.line}: {e.message}</li>)}
              {imported.errors.length > 10 && <li>… und {imported.errors.length - 10} weitere</li>}
            </ul>
          )}
        </div>
      )}

      <div className="card chart-toolbar" role="toolbar" aria-label="Aktionen für den Kontenplan">
        <div className="chart-toolbar__row">
          {canWrite && <button type="button" className="btn btn--primary btn--sm" onClick={() => setEditor({ type: 'account', mode: 'create', categoryId: selectedCategory?.id ?? selectedAccount?.categoryId ?? null })}>Neues Konto</button>}
          {canWrite && <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditor({ type: 'category', mode: 'create', parentId: null })}>Neue Kategorie</button>}
          {canWrite && selectedCategory && selectedCategory.level < 3 && (
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditor({ type: 'category', mode: 'create', parentId: selectedCategory.id })}>Unterkategorie anlegen</button>
          )}
          <span className="chart-toolbar__sep" aria-hidden="true" />
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setCollapsed(new Set())}>Alles aufklappen</button>
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setCollapsed(new Set(allIds))}>Alles zuklappen</button>
          <a className="btn btn--secondary btn--sm" href="/api/v1/chart/export">CSV exportieren</a>
          <a className="btn btn--secondary btn--sm" href="/api/v1/chart/export?format=pdf">PDF exportieren</a>
          {canWrite && (
            <>
              <button type="button" className="btn btn--secondary btn--sm" disabled={importCsv.isPending} onClick={() => fileInput.current?.click()}>CSV importieren</button>
              <input ref={fileInput} type="file" accept=".csv,text/csv,text/plain" hidden onChange={(e) => { void pickFile(e.target.files?.[0]); e.target.value = ''; }} />
            </>
          )}
          {can('setup.write') && <Link className="btn btn--secondary btn--sm" to="/einrichtung/kontenplan">Kontenrahmen wechseln</Link>}
        </div>
        <div className="chart-toolbar__row">
          <label className="chart-search">
            <span className="sr-only">Konto oder Kategorie suchen</span>
            <input className="input" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nummer, Name oder Beschreibung suchen" />
          </label>
          <label className="chart-filter">
            <span className="sr-only">Art</span>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as '' | ChartKind)}>
              <option value="">Alle Arten</option>
              {CHART_KINDS.map((k) => <option key={k} value={k}>{CHART_KIND_LABELS[k]}</option>)}
            </select>
          </label>
          {showTotals && (
            <label className="chart-filter">
              <span className="sr-only">Jahr der Summen</span>
              <select className="input" value={year} onChange={(e) => setYear(e.target.value)} aria-label="Jahr der Summen">
                <option value="">Alle Jahre</option>
                {(totals.data?.years ?? []).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          )}
          <label className="check">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <span>Inaktive zeigen</span>
          </label>
        </div>
        {/* Aktionen zur Auswahl: erscheinen, sobald eine Kategorie oder ein Konto gewählt ist */}
        {(selectedCategory || selectedAccount) && (
          <div className="chart-toolbar__row chart-toolbar__selection" aria-live="polite">
            <strong className="chart-selection__label">{selectedCategory ? `Kategorie „${selectedCategory.name}“` : `Konto ${selectedAccount!.number} ${selectedAccount!.name}`}</strong>
            {canWrite && selectedCategory && (
              <>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditor({ type: 'category', mode: 'edit', id: selectedCategory.id })}>Bearbeiten</button>
                <button type="button" className="btn btn--secondary btn--sm" disabled={siblings(selectedCategory)[0]?.id === selectedCategory.id || action.isPending} onClick={() => run(() => api('POST', `/chart/categories/${selectedCategory.id}/move`, { direction: 'up' }))} aria-label="Eine Stelle nach oben">↑ Nach oben</button>
                <button type="button" className="btn btn--secondary btn--sm" disabled={siblings(selectedCategory).at(-1)?.id === selectedCategory.id || action.isPending} onClick={() => run(() => api('POST', `/chart/categories/${selectedCategory.id}/move`, { direction: 'down' }))} aria-label="Eine Stelle nach unten">↓ Nach unten</button>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditor({ type: 'account', mode: 'create', categoryId: selectedCategory.id })}>Konto hier anlegen</button>
              </>
            )}
            {canDelete && selectedCategory && (
              <button
                type="button"
                className="btn btn--danger btn--sm"
                disabled={action.isPending}
                onClick={() => window.confirm(`Kategorie „${selectedCategory.name}“ löschen? Das geht nur, wenn sie leer ist.`) && (run(() => api('DELETE', `/chart/categories/${selectedCategory.id}`)), setSelection(null))}
              >
                Löschen
              </button>
            )}
            {showTotals && selectedAccount && (
              <Link className="btn btn--secondary btn--sm" to={`/sachposten?from=${year || '2000'}-01-01&to=${year || '2100'}-12-31&account=${selectedAccount.number}`}>Posten anzeigen</Link>
            )}
            {canWrite && selectedAccount && (
              <>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditor({ type: 'account', mode: 'edit', id: selectedAccount.id })}>Bearbeiten</button>
                <button type="button" className="btn btn--secondary btn--sm" disabled={action.isPending} onClick={() => run(() => api('PATCH', `/chart/accounts/${selectedAccount.id}`, { active: !selectedAccount.active }))}>
                  {selectedAccount.active ? 'Deaktivieren' : 'Aktivieren'}
                </button>
              </>
            )}
            {canDelete && selectedAccount && (
              <button
                type="button"
                className="btn btn--danger btn--sm"
                disabled={action.isPending || selectedAccount.usage.total > 0}
                title={selectedAccount.usage.total > 0 ? 'Das Konto wird verwendet. Deaktivieren Sie es stattdessen.' : undefined}
                onClick={() => window.confirm(`Konto ${selectedAccount.number} ${selectedAccount.name} löschen?`) && (run(() => api('DELETE', `/chart/accounts/${selectedAccount.id}`)), setSelection(null))}
              >
                Löschen
              </button>
            )}
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setSelection(null)}>Auswahl aufheben</button>
          </div>
        )}
      </div>

      {editor && (
        <ChartEditor
          key={`${editor.type}-${editor.mode}-${'id' in editor ? editor.id : ''}`}
          editor={editor}
          tree={tree}
          pathOf={pathOf}
          onClose={() => setEditor(null)}
          onSaved={(data) => {
            store(data);
            setEditor(null);
          }}
        />
      )}

      <div className="chart-tree">
        {nodes.length === 0 && <p className="card muted">{query || kind ? 'Nichts gefunden. Ändern Sie die Suche oder den Filter.' : 'Der Kontenplan ist leer. Legen Sie eine Kategorie an oder wechseln Sie den Kontenrahmen.'}</p>}
        {nodes.map((n) => (
          <CategoryNode key={n.category.id} node={n} depth={0} collapsed={query ? new Set() : collapsed} onToggle={toggle} selection={selection} onSelect={setSelection} totals={showTotals ? (totals.data?.accounts ?? {}) : null} counts={deepCounts} />
        ))}
      </div>
      {showTotals && totals.data && (
        <p className="muted small">
          Summen netto aus den Belegen {year ? `des Jahres ${year}` : 'aller Jahre seit Beginn'}, nach {totals.data.basis === 'payment' ? 'Zahlungsdatum' : 'Rechnungsdatum'}. Erlöse bei Erlöskonten, Aufwand bei Sachkonten, Stornos und Gutschriften sind abgezogen. Es ist keine Buchführung, Wirkia bucht nicht.
        </p>
      )}
      <p className="muted small">
        Konten sind Auszüge und Vorschläge, kein Ersatz für die Beratung. Das Konto je Steuerschlüssel stellen Sie in Einrichtung → Finanzen ein, das Sachkonto tragen Sie an der Eingangsrechnung ein.
        <InfoHint label="Verwendung">Ein Konto gilt als verwendet, wenn es als Erlös- oder Steuerkonto je Steuerschlüssel eingestellt ist, an einer Eingangsrechnung als Sachkonto steht oder bei einem Kontakt als Personenkonto. Verwendete Konten lassen sich nicht löschen und nicht umnummerieren, damit alte Belege eindeutig bleiben. Sie lassen sich deaktivieren.</InfoHint>
      </p>
    </>
  );
}

function CategoryNode({ node, depth, collapsed, onToggle, selection, onSelect, totals, counts }: { totals: Totals; counts: Map<string, number>; node: Node; depth: number; collapsed: Set<string>; onToggle: (id: string) => void; selection: Selection; onSelect: (s: Selection) => void }) {
  const c = node.category;
  const open = !collapsed.has(c.id);
  const selected = selection?.type === 'category' && selection.id === c.id;
  const sum = sumOf(node, totals);
  return (
    <section className={`chart-cat chart-cat--l${depth + 1}${selected ? ' is-selected' : ''}`} aria-label={`Kategorie ${c.name}`}>
      <header className="chart-cat__head">
        <button type="button" className="chart-cat__toggle" aria-expanded={open} aria-label={open ? `${c.name} zuklappen` : `${c.name} aufklappen`} onClick={() => onToggle(c.id)}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={open ? 'is-open' : ''}>
            <path d="m9 6 6 6-6 6" />
          </svg>
        </button>
        <button type="button" className="chart-cat__name" aria-pressed={selected} onClick={() => onSelect(selected ? null : { type: 'category', id: c.id })}>
          {c.code && <span className="chart-code">{c.code}</span>}
          <strong>{c.name}</strong>
          {depth === 0 && <span className={`chart-kind chart-kind--${c.kind}`}>{CHART_KIND_LABELS[c.kind]}</span>}
        </button>
        {totals && <span className="chart-sum chart-sum--cat" title={sum ? `${sum.count} Belege` : 'Keine Belege'}>{sum ? formatCents(sum.cents) : '–'}</span>}
        <span className="chart-cat__count muted small">{counts.get(c.id) ?? c.accountCount} {(counts.get(c.id) ?? c.accountCount) === 1 ? 'Konto' : 'Konten'}</span>
      </header>
      {open && (
        <div className="chart-cat__body">
          {node.accounts.length > 0 && (
            <ul className="chart-accounts">
              {node.accounts.map((a) => {
                const sel = selection?.type === 'account' && selection.id === a.id;
                return (
                  <li key={a.id} className={`chart-account${sel ? ' is-selected' : ''}${a.active ? '' : ' is-inactive'}`}>
                    <button type="button" className="chart-account__button" aria-pressed={sel} onClick={() => onSelect(sel ? null : { type: 'account', id: a.id })}>
                      <span className="chart-account__number">{a.number}</span>
                      <span className="chart-account__name">{a.name}</span>
                      <span className="chart-account__meta">
                        {a.defaultTaxCode && <span className="chart-badge" title={TAX_CODES.find((t) => t.code === a.defaultTaxCode)?.label}>{a.defaultTaxCode}</span>}
                        {!a.active && <span className="chart-badge chart-badge--off">inaktiv</span>}
                        {a.source === 'custom' && <span className="chart-badge chart-badge--own">eigen</span>}
                        {a.usage.total > 0 && <span className="chart-badge chart-badge--used" title="Das Konto wird verwendet">verwendet</span>}
                      </span>
                      {totals && <span className="chart-sum" title={totals[a.number] ? `${totals[a.number]!.count} Belege` : 'Keine Belege'}>{totals[a.number] ? formatCents(totals[a.number]!.netCents) : '–'}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {node.children.map((child) => (
            <CategoryNode key={child.category.id} node={child} depth={depth + 1} collapsed={collapsed} onToggle={onToggle} selection={selection} onSelect={onSelect} totals={totals} counts={counts} />
          ))}
          {node.accounts.length === 0 && node.children.length === 0 && <p className="muted small chart-empty">Keine Konten in dieser Kategorie.</p>}
        </div>
      )}
    </section>
  );
}

/** Formular für Kategorie oder Konto, neu und bearbeiten. Verschieben geschieht über die übergeordnete Kategorie beziehungsweise die Kategorie des Kontos. */
function ChartEditor({ editor, tree, pathOf, onClose, onSaved }: { editor: NonNullable<Editor>; tree: ChartTree; pathOf: (id: string) => string; onClose: () => void; onSaved: (tree: ChartTree) => void }) {
  const category = editor.type === 'category' && editor.mode === 'edit' ? tree.categories.find((c) => c.id === editor.id) : undefined;
  const account = editor.type === 'account' && editor.mode === 'edit' ? tree.accounts.find((a) => a.id === editor.id) : undefined;
  const [name, setName] = useState(category?.name ?? account?.name ?? '');
  const [code, setCode] = useState(category?.code ?? '');
  const [kind, setKind] = useState<ChartKind>(category?.kind ?? 'expense');
  const [parentId, setParentId] = useState<string>(editor.type === 'category' ? (editor.mode === 'create' ? (editor.parentId ?? '') : (category?.parentId ?? '')) : '');
  const [number, setNumber] = useState(account?.number ?? '');
  const [description, setDescription] = useState(account?.description ?? '');
  const [categoryId, setCategoryId] = useState<string>(editor.type === 'account' ? (editor.mode === 'create' ? (editor.categoryId ?? '') : (account?.categoryId ?? '')) : '');
  const [taxCode, setTaxCode] = useState(account?.defaultTaxCode ?? '');
  const [active, setActive] = useState(account?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const descendants = (id: string): Set<string> => {
    const out = new Set<string>([id]);
    for (let grew = true; grew; ) {
      grew = false;
      for (const c of tree.categories) if (c.parentId && out.has(c.parentId) && !out.has(c.id)) (out.add(c.id), (grew = true));
    }
    return out;
  };
  const blocked = category ? descendants(category.id) : new Set<string>();
  const parents = tree.categories.filter((c) => c.level < 3 && !blocked.has(c.id));
  const usedAccount = (account?.usage.total ?? 0) > 0;

  const save = useMutation({
    mutationFn: async (): Promise<ChartTree> => {
      if (editor.type === 'category') {
        if (editor.mode === 'create') return (await api<{ data: ChartTree }>('POST', '/chart/categories', { name, code, ...(parentId ? { parentId } : { kind }) })).data;
        let data = (await api<{ data: ChartTree }>('PATCH', `/chart/categories/${editor.id}`, { name, code, ...(category && !category.parentId ? { kind } : {}) })).data;
        if ((category?.parentId ?? '') !== parentId) data = (await api<{ data: ChartTree }>('POST', `/chart/categories/${editor.id}/move`, { parentId: parentId || null, ...(parentId ? {} : { kind }) })).data;
        return data;
      }
      const body = { number: number.trim(), name, description, categoryId, defaultTaxCode: taxCode || null, active };
      if (editor.mode === 'create') return (await api<{ data: ChartTree }>('POST', '/chart/accounts', body)).data;
      return (await api<{ data: ChartTree }>('PATCH', `/chart/accounts/${editor.id}`, body)).data;
    },
    onSuccess: onSaved,
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setError('Bitte prüfen Sie die markierten Felder.');
      } else setError(errorText(e, 'Das Speichern hat nicht geklappt.'));
    },
  });
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setErrors({});
    save.mutate();
  }

  const title =
    editor.type === 'category' ? (editor.mode === 'create' ? (parentId ? 'Neue Unterkategorie' : 'Neue Kategorie') : 'Kategorie bearbeiten') : editor.mode === 'create' ? 'Neues Konto' : `Konto ${account?.number ?? ''} bearbeiten`;

  return (
    <form className="card chart-editor" onSubmit={submit} noValidate aria-label={title}>
      <h2>{title}</h2>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {editor.type === 'category' ? (
        <div className="form-grid">
          <label className="field">
            <span>Name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus aria-invalid={!!errors.name} />
            {err('name')}
          </label>
          <label className="field">
            <span>Kürzel<InfoHint label="Kürzel">Ein kurzes Kennzeichen wie B2 oder 4.1, freiwillig. Es hilft beim CSV-Import, wenn zwei Kategorien denselben Namen tragen.</InfoHint></span>
            <input className="input" value={code} onChange={(e) => setCode(e.target.value)} maxLength={10} aria-invalid={!!errors.code} />
            {err('code')}
          </label>
          <label className="field">
            <span>Übergeordnete Kategorie</span>
            <select className="input" value={parentId} onChange={(e) => setParentId(e.target.value)} aria-invalid={!!errors.parentId}>
              <option value="">Keine (oberste Kategorie)</option>
              {parents.map((c) => <option key={c.id} value={c.id}>{pathOf(c.id)}</option>)}
            </select>
            {err('parentId')}
          </label>
          {!parentId && (
            <label className="field">
              <span>Art<InfoHint label="Art">Die Art gilt für die Kategorie und alle Unterkategorien und Konten darin. Der Finanzreport wertet Erlöse und Aufwand danach aus.</InfoHint></span>
              <select className="input" value={kind} onChange={(e) => setKind(e.target.value as ChartKind)} aria-invalid={!!errors.kind}>
                {CHART_KINDS.map((k) => <option key={k} value={k}>{CHART_KIND_LABELS[k]}</option>)}
              </select>
              {err('kind')}
            </label>
          )}
        </div>
      ) : (
        <div className="form-grid">
          <label className="field">
            <span>Kontonummer</span>
            <input className="input num" inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value)} maxLength={8} disabled={usedAccount} autoFocus aria-invalid={!!errors.number} />
            {usedAccount && <small className="muted">Das Konto wird verwendet und lässt sich nicht umnummerieren.</small>}
            {err('number')}
          </label>
          <label className="field field--wide">
            <span>Bezeichnung</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} aria-invalid={!!errors.name} />
            {err('name')}
          </label>
          <label className="field field--wide">
            <span>Kategorie</span>
            <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-invalid={!!errors.categoryId}>
              <option value="">Bitte wählen</option>
              {tree.categories.map((c) => <option key={c.id} value={c.id}>{pathOf(c.id)}</option>)}
            </select>
            {err('categoryId')}
          </label>
          <label className="field">
            <span>Steuerschlüssel<InfoHint label="Steuerschlüssel">Welcher Steuerschlüssel zu diesem Konto passt, freiwillig. Er dient als Vorschlag, zum Beispiel bei Erlöskonten.</InfoHint></span>
            <select className="input" value={taxCode} onChange={(e) => setTaxCode(e.target.value)} aria-invalid={!!errors.defaultTaxCode}>
              <option value="">Keiner</option>
              {TAX_CODES.map((t) => <option key={t.code} value={t.code}>{t.code}: {t.label}</option>)}
            </select>
            {err('defaultTaxCode')}
          </label>
          <label className="field field--wide">
            <span>Beschreibung</span>
            <textarea className="input textarea textarea--short" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
          </label>
          <label className="field field--check">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Aktiv (erscheint in Auswahllisten)</span>
          </label>
        </div>
      )}
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
        <button type="button" className="btn btn--secondary" onClick={onClose}>Abbrechen</button>
      </div>
    </form>
  );
}
